/**
 * 🐑 **Drawing the sheep** — T255, D117, D124.
 *
 * 🔴 **THIS IS `players.ts` FOR A FOUR-LEGGED ANIMAL**, deliberately and almost
 * line for line: a photographed model, cut into rigid parts, swung by
 * arithmetic, cloned per body, placed against the render origin. ⚠️ **Nothing
 * here is a new idea** — and that is the point, because every one of those
 * pieces has already been proved in the game.
 *
 * ⚠️ **STALE BELOW, KEPT AS HISTORY — the body IS a skinned mesh since T264**
 * (2026-09-24), and since D126 there are TWO of them, woolly and shorn. The
 * standing description is `Development/14_The_Animal_System.md` §③ and §⑧.
 *
 * ## 🔴 What it does NOT have, and it is on purpose
 *
 * **No skeleton, no rig, no `SkinnedMesh`, no `AnimationMixer`, no animation
 * clip.** 📖
 * [`Minecraft/19`](../../../Development/Minecraft/19_Animal_Animation.md) §1.1:
 * **the entire four-legged walk in Minecraft is four legs rotated by ONE cosine
 * at ±80°, diagonal pairs in phase** — read off Mojang's own published files, in
 * both editions. 👉 **That is the whole case against a rigging pipeline**, and
 * D117 settles it.
 *
 * ## ⚠️ What haugur asked for, and what is here tonight
 *
 * 🎯 *"sheep needs to be able to walk aroun, run a little, raise, lower and turn
 * it s hea, we are going for lifelike movement."*
 *
 * | | |
 * |---|---|
 * | 🚶 **Walk and run** | ✅ one gait, **amplitude scaled by a SMOOTHED speed** — the 0.4 lag is what stops the legs snapping |
 * | 🐑 **Turn, raise and lower the head** | ✅ a separate part, Mojang's 75° lead and 10°-a-tick rate |
 * | 🌿 **Eat off the ground** | ✅ the head drops and **CHEWS** — the 2-second animation both editions agree on |
 * | 👂 **Ears · 🐁 tail** | ⬜ **not cut yet.** The ears are measurably there (the head is 1.6× wider at the ear line than at the muzzle); 🔴 **the TAIL is not** — only 4 cm stands proud of the hind legs, because it hangs against the rump in a side-on photograph. **That needs a different source picture, not a better cutter** |
 * | 🛌 **Lie down · jump · fall over** | ⬜ **not built.** Poses, D124 |
 */

import { Box3, Color, Group, type Mesh, type Object3D, Vector3 } from 'three';
import { HEART_AT, heartMotion, makeHeart } from './heartmesh.ts';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import {
  HURT_BARE,
  HURT_FLEECE,
  READY_BARE,
  READY_FLEECE,
  creatureMaterial,
  markFleece,
} from './creaturepaint.ts';
import { TIP_SECONDS, lyingPose } from './sheepfall.ts';
import { uniform } from 'three/tsl';
import type { Bulk, CreaturesMessage } from '@lofi/shared';
import { SHEEP_HEIGHT, SHEEP_LENGTH, SHEEP_SPEED, SHEEP_WIDTH } from '@lofi/shared';
import {
  grazePose,
  grazeStep,
  type GrazeState,
  headTowards,
  limbSpeed,
  quadSwing,
  type HeadPose,
} from './legsplit.ts';
import {
  axesFromMeshSize,
  deriveQuadAnatomy,
  headingTurn,
  type QuadAnatomy,
  type RigBone,
} from './quadrig.ts';
import { bindRig, poseRig, type BoundRig } from './sheeprig.ts';
import { stamped } from './assets.ts';

/** 🐑 Where the model is served from. */
export const SHEEP_URL = stamped('/models/sheep.glb');

/**
 * ✂️ **The SHORN body — D126, D124.** 🎯 haugur's pick of three, *"2"* —
 * `Design/samples/113_shorn_sheep_candidates.png`, seed 876.
 *
 * 🔴 **A SECOND BODY, not a shrunken first one**: generated from words with
 * the woolly sheep's own length and height, then rigged. ⚠️ **Its skeleton is
 * NOT the woolly one's** — different bone names, a tail and no ear bones — so
 * it gets its OWN anatomy, derived exactly the same way.
 */
export const SHEEP_SHORN_URL = stamped('/models/sheep-shorn.glb');

/**
 * 🐣 **THE LAMB — D130.** 🎯 haugur's pick of four, *"lamb 3"* — round, a pale
 * face, and the only one with a tail bone. `Design/samples/136_lamb_candidates.png`.
 *
 * 🔴 **A THIRD BODY, not a shrunken first one** (his choice: *"its own lamb
 * body"*). It is loaded at the ADULT's height and drawn at the fraction the
 * server sends — `creatures[].lamb`, 0.55 at birth to 0.85 in the autumn.
 * ⚠️ **No ear bones**, so its ears do not move — the shorn body's gap too.
 */
export const LAMB_URL = stamped('/models/lamb.glb');

/** Which of the three bodies. */
type Coat = 'woolly' | 'shorn' | 'lamb';

/**
 * 🦴 **Everything one loaded body needs to dress a sheep.**
 *
 * 🔴 **One per MODEL, never one per flock** — since D126 there are two bodies,
 * and each has its own skeleton, so a bone order or an anatomy borrowed from
 * the other would pose the wrong bones.
 */
interface Kit {
  readonly template: Group;
  readonly anatomy: QuadAnatomy;
  readonly boneOrder: readonly string[];
  /** 🩸 Its own material and red twin — built when it loads, never mid-game. */
  readonly materials: { readonly normal: unknown; readonly hurt: unknown; readonly ready: unknown };
}

/** 💙 **The first flash, when the turf is eaten**, seconds — the red flash's length. */
export const READY_FLASH_SECONDS = 0.5;
/** 💙 **A pulse every this many seconds while it stays ready.** A dial. */
export const READY_PULSE_EVERY = 3;
/** 💙 **How long each pulse lasts**, seconds. A dial. */
export const READY_PULSE_SECONDS = 0.35;

/**
 * 💙 **Is a ready sheep blue at this moment?** — D130. Blue for the first
 * {@link READY_FLASH_SECONDS} after it eats, then for {@link READY_PULSE_SECONDS}
 * at every {@link READY_PULSE_EVERY}, for as long as it is ready. When the pulse
 * stops, the thirty seconds are up.
 *
 * @param readyFor seconds since this client first saw it ready; `null` if it is not
 */
export function readyIsBlue(readyFor: number | null): boolean {
  if (readyFor === null) return false;
  if (readyFor < READY_FLASH_SECONDS) return true;
  return readyFor % READY_PULSE_EVERY < READY_PULSE_SECONDS && readyFor >= READY_PULSE_EVERY;
}

/** ☀️ How much daylight the sheep is standing in, 0–1 — driven by `main.ts`. */
export const sheepDaylight = uniform(1);

/** 📏 Beyond this, a sheep is not drawn at all. */
export const SHEEP_VIEW = 90;

/**
 * 📐 **How far the body rolls into a turn, per radian-a-second of turning.**
 *
 * ⚠️ **A DIAL, and not haugur's number.** Small on purpose — the fault that
 * reads worst on an animal is one that wallows.
 */
const LEAN_PER_TURN = 0.09;
/** 📐 And a hard ceiling on it, so a sharp turn cannot tip a sheep over. */
const LEAN_MOST = 0.12;
/**
 * 📏 ⬜ **THE BOB IS NOT BUILT ON THE RIGGED BODY, AND THAT IS A KNOWN GAP.**
 *
 * 🎯 haugur asked for the barrel to rise and fall with the stride (D124), and
 * on the cut body it was 2% of standing height applied to a trunk GROUP that
 * the legs were deliberately NOT in.
 *
 * 🔴 **A skinned mesh has no such group.** Lifting any bone that a hoof hangs
 * from lifts the hoof — and in one of the two rigs measured, the chest is the
 * fore legs' own parent, so there is no bone that moves the barrel and not a
 * leg. ⚠️ **Faking it on the root is exactly the gliding already reported
 * once (#745).**
 *
 * 👉 **So it is left OUT rather than done wrong**, and said so here. The honest
 * fix is a bob that also shortens the legs — real animals bend at the knee —
 * and that is a separate piece of work, not a line smuggled into the body swap.
 */

/**
 * 🎨 **What colour the fleece is TINTED to, and why there is a tint at all.**
 *
 * 🔴 **The model is generated PALE on purpose.** `tools/design-creature-model.mjs`
 * asks Rodin for *a creamy white fleece*, because **the three base colours of an
 * Icelandic sheep — hvítt, svart and mórautt (D124) — have to come off ONE
 * body.** Text-to-3D gives a different body every run, so three colours cannot
 * mean three models: it would be three different animals and three times the
 * triangles.
 *
 * ⚠️ **And the direction matters.** White → black and white → mórautt are a
 * MULTIPLY. **Black → white is not** — you cannot add light that a pixel does
 * not have. 👉 ***So the sheep that PROVED this route (a black one) could never
 * be the sheep that ships.***
 *
 * 📏 **Mórautt today**, which is the colour every sheep in the game already is —
 * the old body was cut from the mórautt candidate. **So this swap changes the
 * SHAPE and deliberately does not change the COLOUR.**
 *
 * 🔬 **AND THE NUMBER WAS MEASURED RATHER THAN EYEBALLED.** Averaged over both
 * models' own albedo atlases, ignoring near-black and near-white pixels so the
 * eyes, hooves and the atlas ground do not drag it:
 *
 * | | |
 * |---|---|
 * | old body, mórautt photograph | **rgb(108, 80, 51)** over 4.0 M pixels |
 * | new body, cream | **rgb(208, 200, 189)** over 4.2 M pixels |
 * | **the ratio** | **×0.520, ×0.400, ×0.272 → `0x856645`** |
 *
 * ⚠️ **A first guess of `0x6f5541` was noticeably too dark**, which is the whole
 * argument for measuring: *a colour nobody can see on this workstation is a
 * colour nobody may guess.*
 *
 * ⏭️ **T263 rolls it per sheep** from the creature's own seeded `Rng`, which is
 * 🎯 haugur's *"we should just randomly use the colors."* ⚠️ **That needs a
 * material PER SHEEP** — `template.clone(true)` shares materials — and this
 * repo has blacked out both environments on a render-path change before, so it
 * is its own ticket rather than a line smuggled into this one.
 */
const FLEECE = uniform(new Color(0x856645));

/**
 * 🧴 **What the BARE parts are tinted to — the face, the ears and the legs.**
 *
 * 🎯 **haugur:** *"they are the same brown colour from head to toe, not very
 * realistic like that."*
 *
 * 🔴 **Near white on purpose, which means *leave the albedo alone*.** The model
 * is generated with a pale face and pale hooves already, so the honest fix is
 * **to stop tinting them**, not to paint them a second colour. ⚠️ **A little
 * under white** so the bare skin is not brighter than the fleece it joins.
 *
 * ⚠️ **A DIAL, and not haugur’s number.** It is one line to change once he has
 * looked at a sheep with a face.
 */
const BARE = uniform(new Color(0xe6dfd4));

/**
 * 🩸 **A struck sheep flashes RED — D125, 2026-09-28.** 🎯 haugur: *"there need
 * to be somekind of indication that im hurting it, like it flashing red when i
 * hit it."* 📖 Minecraft tints a hurt mob red for its **0.5 s** hurt window.
 *
 * 🔴 **A SECOND INSTANCE OF THE SAME MATERIAL, BUILT WHEN THE MODEL LOADS, and
 * a struck sheep is SWAPPED onto it.** ⚠️ **Never a material changed while the
 * game runs** — a plain material mutated into a textured one is what blacked out
 * dev AND staging once. This is the creature's own `creatureMaterial`, with two
 * red tints in place of the fleece and skin ones — `HURT_FLEECE` and
 * `HURT_BARE` in `creaturepaint.ts`, shared with `rigsheet.html`.
 */

/** 🩸 How long the red lasts, seconds — Minecraft's hurt window. */
const HURT_FLASH_SECONDS = 0.5;

/** 📏 How fast a drawn body chases where the server put it. */
const CHASE = 7;

/** One sheep as it is being drawn. */
interface Live {
  readonly id: string;
  /** Where the server last said it was. */
  tx: number;
  ty: number;
  tz: number;
  tyaw: number;
  /** Where it is being drawn, chasing the above. */
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** What the server says it is doing — `Graze`, `Wander`, `Follow`. */
  doing: string | null;
  /** 👀 What it is LOOKING at, by name — `Watch`, `LookAround`, or null. */
  looking: string | null;
  /** 👀 WHERE it is looking, in world metres, or `null` for straight ahead. */
  lookAt: { x: number; y: number; z: number } | null;
  /** 🚶 Total metres walked, which is what drives the gait. */
  walked: number;
  /** 🏃 The smoothed 0–1 the swing amplitude is scaled by. */
  gait: number;
  /** 🐑 Where its head is pointing, relative to its body. */
  head: HeadPose;
  /** 🌿 Where its grazing head is, or `null` when it is up. */
  grazing: GrazeState | null;
  /** 🔄 How fast it is turning, radians a second, smoothed — what the lean reads. */
  turning: number;
  /** 💀 How far it has fallen over, 0 standing → 1 on its side. D125. */
  fallen: number;
  /** 🩸 Blows landed, as the server last said — a rise means flash. Protocol 39. */
  hits: number;
  /** 🩸 Seconds of red left. 0 = its own colours. */
  flash: number;
  /** 🩸 The one skinned mesh in this sheep's body, whose material is swapped. */
  skin: { material: unknown } | null;
  /** ✂️ What the server says it is — protocol 40. */
  shorn: boolean;
  /** 🪢 Whose lead it is on — a connection id, protocol 42. */
  ledBy: string | null;
  /** 🪵 The peg it is tied to, as a cell — protocol 42. */
  tether: { x: number; y: number; z: number } | null;
  /** 🐣 A lamb's size as a fraction of an adult's, or `null` for a grown sheep. */
  lamb: number | null;
  /** 💙 Seconds since it was first seen ready to mate, or `null` — D130. */
  readyFor: number | null;
  /** 💙 Ready, as the last beat said. */
  ready: boolean;
  /** ❤️ Mated this season, as the last beat said — and its heart, while it has one. */
  mated: boolean;
  heart: Mesh | null;
  /** ✂️ The body it is WEARING, which lags `shorn` until that model has loaded. */
  kit: Kit | null;
  /** The cloned body inside `object`, so a change of coat can take it out. */
  body: Object3D | null;
  object: Group;
  /** 🦴 This body's own bones, bound once when it is dressed. */
  rig: BoundRig | null;
}

/**
 * 🐑 **The flock** — every sheep the server has told us about.
 *
 * ⚠️ **The same shape as `Shoal`**: `setFrom` takes the server's word, `update`
 * runs every frame, and `stats` goes into an F8 report.
 */
export class Flock {
  readonly group = new Group();
  private readonly live = new Map<string, Live>();
  /** 🦴 The two bodies, each once it has loaded — D126. */
  private readonly kits = new Map<Coat, Kit>();
  private origin = { x: 0, y: 0, z: 0 };
  private measured: Record<string, unknown> = { ready: false };
  private measuredShorn: Record<string, unknown> = { ready: false };
  private measuredLamb: Record<string, unknown> = { ready: false };
  /** ❤️ Seconds, for the hearts' turn and bob — one clock for all, so they move together. */
  private clock = 0;

  constructor(
    url: string = SHEEP_URL,
    shornUrl: string = SHEEP_SHORN_URL,
    lambUrl: string = LAMB_URL,
  ) {
    void this.load(url, 'woolly');
    void this.load(shornUrl, 'shorn');
    void this.load(lambUrl, 'lamb');
  }

  /**
   * 🔴 **Load the model, MEASURE it, and READ ITS SKELETON** — once, at load.
   *
   * ⚠️ **Nothing is baked into a file.** `house-walls.json` is measured from
   * `bur.glb` and then never re-read, and a replaced model leaves every torch
   * hanging off the old one's walls. 🔴 **A creature has far more of those
   * numbers, and the skeleton is the worst of them**: the same sheep rigged
   * twice came back with different bone names, a different tree and a different
   * depth. **So the anatomy is derived here, from this file, every load.**
   */
  private async load(url: string, coat: Coat): Promise<void> {
    const note = (m: Record<string, unknown>): void => {
      if (coat === 'woolly') this.measured = m;
      else if (coat === 'shorn') this.measuredShorn = m;
      else this.measuredLamb = m;
    };
    try {
      const gltf = await new GLTFLoader().loadAsync(url);
      const scene = gltf.scene;
      scene.updateMatrixWorld(true);

      /* ── The skinned mesh, and the albedo it wears ──────────────────────── */
      let skinned: Object3D | null = null;
      let map: unknown = null;
      scene.traverse((o) => {
        const any = o as {
          isSkinnedMesh?: boolean;
          isMesh?: boolean;
          material?: { map?: unknown };
        };
        if (any.isSkinnedMesh === true && skinned === null) skinned = o;
        if (any.isMesh === true && any.material?.map && map === null) map = any.material.map;
      });

      if (skinned === null) {
        /* 🔴 LOUD. A model with no skin draws a sheep frozen in its rest pose,
           which reads as "the animation is broken" and is not. */
        console.error(`[sheep] no SkinnedMesh in the ${coat} model — it is not rigged`);
        note({ ready: false, reason: 'no SkinnedMesh' });
        return;
      }

      /* ── 📏 Size and footing, both MEASURED ─────────────────────────────
         🔴 **The rigger hands the body back 1.78× too big** — UniRig normalises
         the mesh and never restores the scale. ✅ So measure the length and
         divide by it. ⚠️ **Never hardcode the factor**: it is a property of
         whatever came back from the rigger, not of sheep. */
      const size = new Box3().setFromObject(scene).getSize(new Vector3()).toArray();
      const axes = axesFromMeshSize(size);
      const lengthNow = size[axes.length] ?? 1;
      /* 🐣 A lamb is sized by HEIGHT, to the adult's — its own proportions are
         the point (a big head, long legs), so matching LENGTH would make it
         squat. The per-sheep `lamb` fraction then shrinks it (D130). */
      const heightNow = size[axes.up] ?? 1;
      const scale =
        coat === 'lamb'
          ? heightNow > 0
            ? SHEEP_HEIGHT / heightNow
            : 1
          : lengthNow > 0
            ? SHEEP_LENGTH / lengthNow
            : 1;

      const holder = new Group();
      holder.add(scene);
      scene.scale.setScalar(scale);
      holder.updateMatrixWorld(true);
      /* 🦶 Hooves on the ground, off the model's own lowest point — not
         `height / 2`, which is the guess that hung a board 39 cm off a wall. */
      const floor = new Box3().setFromObject(holder).min.y;
      scene.position.y -= floor;
      holder.updateMatrixWorld(true);

      /* ── 🦴 The anatomy, derived from THIS skeleton ─────────────────────── */
      const bones: Object3D[] = [];
      scene.traverse((o) => {
        if ((o as { isBone?: boolean }).isBone === true) bones.push(o);
      });
      const order = bones.map((b) => b.name);
      const at = new Vector3();
      const plain: RigBone[] = bones.map((b) => {
        b.getWorldPosition(at);
        return {
          name: b.name,
          parent: b.parent ? order.indexOf(b.parent.name) : -1,
          world: [at.x, at.y, at.z] as [number, number, number],
        };
      });
      const anatomy = deriveQuadAnatomy(plain, axes);

      /* 🔴 **THE MATERIAL IS BUILT AFTER THE ANATOMY, AND THAT ORDER IS
         LOAD-BEARING.** The fleece/bare split is read off the rig, so the
         anatomy has to exist first. ⚠️ Written the other way round it
         typechecks as a use-before-declaration and would have shipped a sheep
         with no face if the compiler had been any looser. */
      /* 🔴 **THE SAME MATERIAL THE GAME ALREADY COMPILES.** `NodeMaterial`
         applies `skinning()` by itself to any `SkinnedMesh`, so a skinned body
         needs NO new material path — and inventing one is the trap that blacked
         out dev AND staging once. ⚠️ Read in three.js's own `setupPosition`,
         not assumed. */
      /* 🧶 One place paints a creature, and the sample sheet calls the same
         function — see `creaturepaint.ts`. */
      const painted = markFleece(skinned, anatomy, plain);
      if (!painted.split)
        console.warn(
          `[sheep] no skin weights in the ${coat} model — the fleece tint covers the whole body`,
        );
      const normal = creatureMaterial(map, FLEECE, BARE, sheepDaylight);
      (skinned as { material: unknown }).material = normal;
      /* 🩸 The red twin, built NOW with the rest — never made mid-game. */
      const materials = {
        normal,
        hurt: creatureMaterial(map, HURT_FLEECE, HURT_BARE, sheepDaylight),
        /* 💙 And the blue twin — D130, the turf landed. Built now, like the red. */
        ready: creatureMaterial(map, READY_FLEECE, READY_BARE, sheepDaylight),
      };
      /* ⚠️ A skinned body’s bounding box is its REST pose, so a walking sheep
         at the edge of the view culls itself mid-stride. */
      (skinned as { frustumCulled: boolean }).frustumCulled = false;

      /**
       * 🧭 **TURN THE MODEL TO FACE THE WAY THIS GAME MEANS, AND MEASURE IT.**
       *
       * 🎯 **haugur, 2026-09-24:** *"they move their legs but thei glide
       * sideways, like they are missing the brain, similar problem as we had
       * with fish that swam sideways."* **He is right and it was the body, not
       * the brain.**
       *
       * 🔴 **A creature’s heading is `atan2(dz, dx)` and the root is drawn at
       * `-yaw`, so THE NOSE MUST POINT ALONG +X.** ⚠️ **The rigged model’s nose
       * points along +Z** — a quarter turn out, which is exactly a sheep
       * walking sideways.
       *
       * ⚠️ **The cut body used to be rotated into the game’s frame by
       * `toBodyFrame`, and that step was dropped when the body became skinned.**
       * 👉 ***A correction nobody can see in a number is a correction that
       * leaves with the code it lived in.***
       *
       * ✅ **So it is MEASURED off the skeleton rather than hardcoded**: the
       * head end minus the tail end, flattened, turned to +X. **The next
       * animal gets this free whichever way its generator happened to point
       * it.**
       */
      const turnToX = headingTurn(plain, anatomy);
      holder.rotation.y = turnToX;
      holder.updateMatrixWorld(true);
      const faced = Number(((turnToX * 180) / Math.PI).toFixed(1));

      this.kits.set(coat, { template: holder, anatomy, boneOrder: order, materials });
      note({
        ready: true,
        bones: bones.length,
        scale: Number(scale.toFixed(4)),
        modelLength: Number(lengthNow.toFixed(3)),
        legs: anatomy.legs.length,
        neck: anatomy.neck.length,
        ears: anatomy.ears.length,
        tail: anatomy.tail?.length ?? 0,
        /* 🔬 A sheep with three legs is a fault worth seeing in an F8 report
           rather than in a screenshot. */
        faced,
        fleeceShare: painted.fleeceShare,
        legBones: anatomy.legs.map((l) => l.bones.length).join('/'),
      });
      /* 🐑 Nothing to fill here: `update` dresses every sheep that is wearing
         the wrong body — or none — on the next frame. */
    } catch (err: unknown) {
      /* ⚠️ Loud. With no woolly body there are no sheep; with no SHORN body a
         shorn sheep is drawn woolly, which is wrong but still a sheep. */
      console.error(`[sheep] the ${coat} model did not load`, err);
    }
  }

  /** 🔴 Where the scene's origin is, so a body is drawn relative to it. */
  setOrigin(origin: { x: number; y: number; z: number }): void {
    this.origin = origin;
  }

  /** Take the server's word. ⚠️ Only the sheep in it; other creatures are not ours. */
  setFrom(message: CreaturesMessage, _now: number): void {
    const seen = new Set<string>();
    for (const c of message.creatures) {
      if (c.kind !== 'kind') continue;
      seen.add(c.id);
      const had = this.live.get(c.id);
      if (had === undefined) {
        const made: Live = {
          id: c.id,
          tx: c.x,
          ty: c.y,
          tz: c.z,
          tyaw: c.yaw,
          x: c.x,
          y: c.y,
          z: c.z,
          yaw: c.yaw,
          doing: c.doing ?? null,
          looking: c.looking ?? null,
          lookAt: c.lookAt ?? null,
          walked: 0,
          gait: 0,
          head: { yaw: 0, pitch: 0 },
          grazing: null,
          turning: 0,
          fallen: 0,
          /* ⚠️ Whatever it arrives with is NOT a new hit — a sheep struck before
             you came near must not flash the moment you see it. */
          hits: c.hits ?? 0,
          flash: 0,
          skin: null,
          shorn: c.shorn === true,
          ledBy: c.ledBy ?? null,
          tether: c.tether ?? null,
          lamb: c.lamb ?? null,
          /* ⚠️ Arriving already ready is not "it just ate" — no first flash,
             straight into the pulse, the rule the red flash follows for a hit. */
          readyFor: c.ready === true ? READY_FLASH_SECONDS : null,
          ready: c.ready === true,
          mated: c.mated === true,
          heart: null,
          kit: null,
          body: null,
          object: new Group(),
          rig: null,
        };
        this.live.set(c.id, made);
        this.group.add(made.object);
        this.dress(made);
      } else {
        had.tx = c.x;
        had.ty = c.y;
        had.tz = c.z;
        had.tyaw = c.yaw;
        had.doing = c.doing ?? null;
        had.looking = c.looking ?? null;
        had.lookAt = c.lookAt ?? null;
        /* 🩸 It went UP — a blow landed since the last beat. */
        const hits = c.hits ?? 0;
        if (hits > had.hits) had.flash = HURT_FLASH_SECONDS;
        had.hits = hits;
        /* ✂️ Protocol 40. `update` changes the body; this only records it. */
        had.shorn = c.shorn === true;
        /* 🪢🪵 Protocol 42. */
        had.ledBy = c.ledBy ?? null;
        had.tether = c.tether ?? null;
        /* 🐣 Protocol 44 — a lamb, or grown today. `update` swaps the body. */
        had.lamb = c.lamb ?? null;
        /* 💙 Protocol 45. The moment it turns ready is the flash. */
        had.ready = c.ready === true;
        /* ❤️ Protocol 46. */
        had.mated = c.mated === true;
      }
    }
    for (const [id, one] of this.live)
      if (!seen.has(id)) {
        this.group.remove(one.object);
        this.live.delete(id);
      }
  }

  /**
   * 🙌 **A sheep to put in somebody's arms** — T261, protocol 37.
   *
   * 🔴 **The SAME template and the SAME rig the field uses**, so a carried sheep
   * is the animal you just picked up and not a second model that drifts away
   * from it. ⚠️ *A second way in forgets what the first one does*, six times in
   * this repo.
   *
   * 🔴 **`SkeletonUtils.clone`, never `Object3D.clone`** — a plain clone shares
   * the skeleton, so a carried sheep would move with every sheep in the field.
   *
   * ⚠️ **It returns `null` until the model has loaded**, which is the ordinary
   * case for the first second of a session. **The caller must ask again rather
   * than cache the `null`** — the failure otherwise is arms that stay empty for
   * the whole session and nothing anywhere saying why.
   *
   * 🔴 **THE CALLER MUST NEVER DISPOSE WHAT COMES BACK.** The geometry and the
   * materials belong to the template. `players.ts` freeing a borrowed body is
   * the exact fault that once made every arriving player invisible.
   */
  /**
   * 🧍 **Every sheep as a box you can walk into** — T274.
   *
   * 🔴 **Where it is DRAWN, not where the server last said.** You stop against
   * the sheep you can see; stopping against one 30 cm further on would read as
   * an invisible wall.
   *
   * ⚠️ **The same width and height the server's `sheep()` body uses** — both
   * are read off `SHEEP_WIDTH` and `SHEEP_HEIGHT`, so they cannot drift apart.
   * A carried sheep is not here: the server takes it out of the flock.
   */
  bodies(): Bulk[] {
    const out: Bulk[] = [];
    for (const one of this.live.values()) {
      /* 💀 A sheep lying on its side does not block you — D125. */
      if (one.doing === 'Dead') continue;
      out.push({ x: one.x, y: one.y, z: one.z, half: SHEEP_WIDTH / 2, height: SHEEP_HEIGHT });
    }
    return out;
  }

  carriedBody(): Object3D | null {
    /* ⚠️ `null` until the model has loaded — an ordinary answer for the first
       second of a session, and the caller asks again every frame. */
    /* ⚠️ ALWAYS WOOLLY, and that is a known gap (D126): the carry messages do
       not say whether the sheep in somebody's arms was shorn. */
    const kit = this.kits.get('woolly');
    if (kit === undefined) return null;
    const body = cloneSkinned(kit.template);
    /* 🛌 Posed at rest and held still: a sheep in somebody's arms is not
       walking, and a swinging leg would read as a struggle. */
    poseRig(bindRig(body, kit.anatomy, kit.boneOrder), {
      swing: 0,
      headYaw: 0,
      headPitch: 0,
      tailWag: 0,
      lean: 0,
    });
    return body;
  }

  /**
   * 🐑 **Put the right body on a sheep** — the woolly one, or the shorn one
   * (D126). ⚠️ **Until the shorn model has loaded a shorn sheep wears the
   * woolly body**, and the next frame after it loads puts it right.
   */
  private dress(one: Live): void {
    /* 🐣 A lamb wears the lamb body — and on 1 October, when `lamb` stops
       arriving, the woolly one. ⚠️ Until the lamb model has loaded, a lamb is
       drawn woolly, small; wrong for a second, never missing. */
    const want: Coat =
      one.lamb !== null && this.kits.has('lamb')
        ? 'lamb'
        : one.shorn && this.kits.has('shorn')
          ? 'shorn'
          : 'woolly';
    const kit = this.kits.get(want);
    if (kit === undefined || one.kit === kit) return;
    /* ✂️ A change of coat takes the old body OUT. ⚠️ Its geometry and
       materials belong to the template and must never be disposed. */
    if (one.body !== null) one.object.remove(one.body);
    /* 🔴 **`SkeletonUtils.clone`, NEVER `Object3D.clone`.** A plain clone SHARES
       the skeleton, so every sheep in the world would move as one animal —
       ⚠️ **and it would look like the server had stopped sending positions**,
       which is a completely different bug to go hunting for. */
    const body = cloneSkinned(kit.template);
    one.object.add(body);
    one.body = body;
    one.kit = kit;
    one.rig = bindRig(body, kit.anatomy, kit.boneOrder);
    /* 🩸 Remember the skinned mesh, so a hit can swap its material. */
    one.skin = null;
    body.traverse((node) => {
      if ((node as { isSkinnedMesh?: boolean }).isSkinnedMesh === true && one.skin === null) {
        one.skin = node as unknown as { material: unknown };
      }
    });
  }

  /**
   * 🐑 **Every frame: chase the server, then move the body.**
   *
   * 🔴 **The chase must not be throttled**, or the smoothing is what stutters.
   */
  /* 👀 The player position is no longer needed: the SERVER says where a head
     is pointing (protocol 36), so the client never guesses. */
  update(dt: number): void {
    const chase = Math.min(1, dt * CHASE);
    this.clock += dt;
    for (const one of this.live.values()) {
      /* ✂️ Shorn, grown back, or a model that has just finished loading. */
      this.dress(one);
      const wasX = one.x;
      const wasZ = one.z;
      one.x += (one.tx - one.x) * chase;
      one.y += (one.ty - one.y) * chase;
      one.z += (one.tz - one.z) * chase;
      /* ⚠️ The shortest way round, or a sheep turning past due south spins the
         long way — which reads as an animal having a fit. */
      const delta = ((one.tyaw - one.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      one.yaw += delta * chase;

      /* 🚶 Distance walked is what drives the gait — not time. A slow walk
         swings slowly and a run swings fast, with nobody tuning a second
         number, and the legs STOP DEAD when it stands still. */
      const step = Math.hypot(one.x - wasX, one.z - wasZ);
      one.walked += step;
      /* 🔴 Against the SHEEP’s own top speed, not Mojang’s 5 m/s sprinting
         player — which made the legs swing about three degrees (#745). */
      one.gait = limbSpeed(one.gait, dt > 0 ? step / dt : 0, dt, SHEEP_SPEED);

      /* 🌿 Grazing: the server says so by name, and the name is on the wire for
         exactly this. `Minecraft/18` §1: the behaviour in charge has a NAME. */
      one.grazing = grazeStep(one.grazing, one.doing === 'Graze', dt);

      /* 📏 One step, from the animal's own size. A dial. */
      const stride = SHEEP_LENGTH * 0.42;

      const root = one.object;
      root.position.set(one.x - this.origin.x, one.y - this.origin.y, one.z - this.origin.z);
      root.rotation.y = -one.yaw;

      /**
       * 💀 **IT FALLS OVER — D125.** 🎯 His choice: *"falls over, meat to pack."*
       *
       * 🔴 **The WHOLE animal rolls onto its side, about its own length**, which
       * needs no new pose in the rig: a dead sheep's legs are stiff, and that is
       * what a tipped-over body with its legs straight looks like.
       *
       * ⚠️ **`YXZ`, not three.js's default `XYZ`.** The default applies the roll
       * AFTER the yaw, in the world's frame — so a sheep facing north would roll
       * forward onto its nose instead of onto its side. **Yaw first, then roll
       * about the body's own long axis.**
       *
       * ⚠️ **And it is LIFTED by half its width as it goes**, or half the body
       * would lie below the grass. The server keeps it for `DEATH_SECONDS`, and
       * then it is simply absent from the next beat.
       */
      one.fallen = one.doing === 'Dead' ? Math.min(1, one.fallen + dt / TIP_SECONDS) : 0;
      /* 🩸 Red for half a second after a hit, then its own colours again.
         ⚠️ Assigned only when it CHANGES — the material is swapped, never
         edited. */
      if (one.skin !== null && one.kit !== null) {
        one.flash = Math.max(0, one.flash - dt);
        /* 💙 D130 — ready to mate: blue once, then a pulse. Red wins over blue:
           a sheep struck while ready is a struck sheep. */
        one.readyFor = one.ready ? (one.readyFor ?? -dt) + dt : null;
        const want =
          one.flash > 0
            ? one.kit.materials.hurt
            : readyIsBlue(one.readyFor)
              ? one.kit.materials.ready
              : one.kit.materials.normal;
        if (one.skin.material !== want) one.skin.material = want;
      }

      const lying = lyingPose(one.fallen);
      root.rotation.order = 'YXZ';
      root.rotation.x = lying.roll;
      root.position.y += lying.lift;
      /* 🐣 A lamb's size — D130. ⚠️ Set every frame, because the same body
         object grows day by day and is re-dressed on the day it is grown. */
      root.scale.setScalar(one.lamb ?? 1);
      /* 🐑💨 Frolic: a skip — 🎯 *"skips and plays"*. Hops tied to the ground
         covered, like the legs, so a skip is as quick as the dash under it;
         0.25 m at an adult's size, scaled with the lamb. */
      /* ❤️ D130 — a heart over a sheep that has mated, turning and bobbing.
         Put on and taken off as the flag changes; hidden on a dead sheep. */
      const wantHeart = one.mated && one.doing !== 'Dead';
      if (wantHeart && one.heart === null) {
        one.heart = makeHeart();
        root.add(one.heart);
      } else if (!wantHeart && one.heart !== null) {
        root.remove(one.heart);
        one.heart = null;
      }
      if (one.heart !== null) {
        const motion = heartMotion(this.clock);
        one.heart.position.y = HEART_AT.up + motion.lift;
        one.heart.rotation.y = motion.turn;
      }
      if (one.doing === 'Frolic') {
        root.position.y +=
          Math.abs(Math.sin((one.walked / stride) * Math.PI)) * 0.25 * (one.lamb ?? 1);
      }

      if (one.rig === null) continue;

      /**
       * 🔄 **THE BODY LEANS — and Minecraft does NOT.**
       *
       * 🎯 **haugur chose it (D124).** 📖 `Minecraft/19` §1.1 is explicit that
       * nothing else on a quadruped moves, so this is one of the few places we
       * deliberately do MORE than Mojang rather than less.
       *
       * 🔴 **It rolls the SPINE BONES, never the whole animal.** On the cut body
       * this was a rotation of a trunk GROUP; a skinned mesh has no groups in
       * it, it is one surface. ⚠️ Rolling the root would swing the hooves out
       * sideways, which is the gliding haugur already reported once (#745).
       */
      const rate = dt > 0 ? delta / dt : 0;
      one.turning += (rate - one.turning) * Math.min(1, dt * 4);
      const lean = Math.max(-LEAN_MOST, Math.min(LEAN_MOST, one.turning * LEAN_PER_TURN));

      /* 🐑 The head. Grazing takes it; otherwise it looks where the server says.
         🔴 **The server says WHERE** (protocol 36) — the client never guesses,
         because two players would each see the sheep looking at themselves. */
      const eating = one.grazing !== null ? grazePose(one.grazing) : null;
      /**
       * 🌿 **THE HEAD MUST REACH THE GRASS — and the LOOK does not do it.**
       *
       * 🎯 **haugur, #820:** *"it bows its head to chest heihgt and bobs
       * around."* 🔴 **This used to be a bigger head PITCH** (36° plus the drop
       * as bend), and a pitch can never get there: 📏 the neck's root is 0.60 m
       * up and neck plus head reach 0.38. ✅ **The graze is its own pose now**
       * — shoulders, neck, stretch and skull, `GRAZE_BODY` in `sheeprig.ts` —
       * so while eating the look is simply let go to straight ahead.
       */
      let wantPitch = 0;
      let wantYaw = 0;
      if (eating === null && one.lookAt !== null) {
        const dx = one.lookAt.x - one.x;
        const dz = one.lookAt.z - one.z;
        /* 🔴 RELATIVE TO THE BODY, because that is what the 75° clamp is about:
           a head may lead its BODY, not the world. */
        const toward = Math.atan2(dz, dx);
        const away = ((toward - one.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        /* 🔴 NEGATED, because the root is drawn at `-yaw`: inside it, a left
           turn in the world is a right turn in the model. ⚠️ Backwards makes a
           sheep pointedly look AWAY from you, which reads as a bug. */
        wantYaw = -away;
        const flat = Math.hypot(dx, dz);
        wantPitch =
          -Math.atan2(one.lookAt.y - (one.y + SHEEP_HEIGHT * 0.7), Math.max(flat, 0.5)) * 0.6;
      }
      one.head = headTowards(one.head, wantYaw, wantPitch, dt);

      /**
       * 🦵 **THE WALK, AND IT IS THE SAME ARITHMETIC AS BEFORE.**
       *
       * 📐 `quadSwing` is unchanged and still tested — one cosine, diagonal
       * pairs, `+ − − +`. 🔴 **What changed is only WHAT IT TURNS**: bones
       * instead of four cut-off leg parts. The magnitude is the pair's own
       * first value, and `poseRig` applies the sign from the derived anatomy,
       * because **which leg is which is no longer a fixed array order.**
       */
      const swing = quadSwing(one.walked, stride, one.gait)[0] ?? 0;

      poseRig(one.rig, {
        swing,
        headYaw: one.head.yaw,
        headPitch: one.head.pitch,
        /* 🐁 The tail only moves when the animal does — a wagging tail on a
           standing sheep reads as a dog. */
        tailWag: one.gait,
        lean,
        graze: eating?.down ?? 0,
        chew: eating?.chew ?? 0,
      });
    }
  }

  /**
   * 🪢 **Every sheep on a lead or a peg, with where its NECK is** — D128, in
   * world metres. `main.ts` pairs each with the hand or the peg and draws the
   * rope.
   *
   * ⚠️ **The neck is read off the POSED skeleton**, so call this after
   * {@link Flock.update}. Before the rig is bound it falls back to a point
   * ahead of the body at neck height.
   */
  tethered(): readonly {
    id: string;
    ledBy: string | null;
    tether: { x: number; y: number; z: number } | null;
    neck: { x: number; y: number; z: number };
    feet: number;
  }[] {
    const out: {
      id: string;
      ledBy: string | null;
      tether: { x: number; y: number; z: number } | null;
      neck: { x: number; y: number; z: number };
      feet: number;
    }[] = [];
    const at = new Vector3();
    for (const one of this.live.values()) {
      if (one.ledBy === null && one.tether === null) continue;
      const bone = one.rig?.neck.at(0)?.bone;
      let neck: { x: number; y: number; z: number };
      if (bone !== undefined) {
        bone.getWorldPosition(at);
        neck = { x: at.x + this.origin.x, y: at.y + this.origin.y, z: at.z + this.origin.z };
      } else {
        neck = {
          x: one.x + Math.cos(one.yaw) * SHEEP_LENGTH * 0.38,
          y: one.y + SHEEP_HEIGHT * 0.7,
          z: one.z + Math.sin(one.yaw) * SHEEP_LENGTH * 0.38,
        };
      }
      out.push({ id: one.id, ledBy: one.ledBy, tether: one.tether, neck, feet: one.y });
    }
    return out;
  }

  /** 🔬 What the flock drew, and what the model measured — into an F8 report. */
  get stats(): Record<string, unknown> {
    /**
     * 🔴 **THE RIG'S OWN NUMBERS, BECAUSE "THE SHEEP LOOKS FROZEN" HAS FOUR
     * DIFFERENT CAUSES AND A SCREENSHOT CANNOT TELL THEM APART.**
     *
     * | What it could be | What this shows |
     * |---|---|
     * | **The model is not rigged** | `ready` is false, `reason` says so |
     * | **The anatomy came out wrong** | `legs` is not 4, or `legBones` is lopsided |
     * | **The bones are not in the clone** | `bound.legs` is 0 |
     * | **It IS posing and the angle is tiny** | `headYaw`, `gait` |
     *
     * ⚠️ **Three times in one day an instrument reported a broken world and was
     * itself the fault.** This one is cheap and it settles the question.
     */
    const first = this.live.values().next().value;
    return {
      ...this.measured,
      /* ✂️ D126 — the shorn body's own numbers, and how many wear it. */
      shornModel: this.measuredShorn,
      lambModel: this.measuredLamb,
      shorn: [...this.live.values()].filter((one) => one.shorn).length,
      drawn: this.live.size,
      looking: first?.looking ?? null,
      doing: first?.doing ?? null,
      bound: first?.rig?.found ?? null,
      headYaw: first ? Number(first.head.yaw.toFixed(4)) : null,
      headPitch: first ? Number(first.head.pitch.toFixed(4)) : null,
      gait: first ? Number(first.gait.toFixed(3)) : null,
    };
  }

  dispose(): void {
    this.group.clear();
    this.live.clear();
  }
}
